/**
 * Gift with purchase (GWP)
 *
 * snippets/cart--gwp.liquid decides which gift the cart qualifies for and
 * renders the result into <gwp-cart> as JSON. This file only applies it:
 * removing gift lines that no longer qualify and, with auto-add on, adding the
 * earned gift. The cart drawer is re-rendered after every cart change, so
 * <gwp-cart> reconnects with fresh state each time.
 */
const GWP = {
  busy: false,
  lastAction: null,
  sections: ['cart', 'cart-count'],
  memory: {},

  // Browser storage with an in-memory fallback (private mode, blocked storage)
  read(key, storage = sessionStorage) {
    try {
      return JSON.parse(storage.getItem(key)) || [];
    } catch (e) {
      return this.memory[key] || [];
    }
  },
  write(key, list, storage = sessionStorage) {
    this.memory[key] = list;
    try {
      storage.setItem(key, JSON.stringify(list));
    } catch (e) {}
  },

  // Gifts the customer removed themselves are not auto-added again. Kept in
  // local storage so it holds across tabs and visits, and cleared once the
  // cart is empty (order placed, or cart emptied) so the next cart starts fresh.
  isDeclined(campaign) {
    return this.read('gwpDeclined', localStorage).includes(campaign);
  },
  setDeclined(campaign, declined) {
    const list = this.read('gwpDeclined', localStorage).filter((handle) => handle !== campaign);
    if (declined) list.push(campaign);
    this.write('gwpDeclined', list, localStorage);
  },
  clearDeclined() {
    if (this.read('gwpDeclined', localStorage).length) this.write('gwpDeclined', [], localStorage);
  },

  track(event, campaign) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, gwp_campaign: campaign });
  },

  trackQualified(campaign) {
    if (!campaign) return;
    const seen = this.read('gwpQualified');
    if (seen.includes(campaign)) return;
    this.write('gwpQualified', [...seen, campaign]);
    this.track('gwp_qualified', campaign);
  },

  reconcile(state) {
    let action = null;

    if (Object.keys(state.updates).length) {
      action = { type: 'update', body: { updates: state.updates } };
    } else if (state.add && !this.isDeclined(state.add.campaign)) {
      action = this.addAction(state.add.id, state.add.campaign);
    }

    if (!action) {
      this.lastAction = null;
      return;
    }

    // Never repeat the same action back to back, so a failing add or a
    // discount mismatch can't loop.
    const signature = JSON.stringify(action);
    if (this.busy || signature === this.lastAction) return;
    this.lastAction = signature;
    this.request(action);
  },

  addAction(id, campaign) {
    return {
      type: 'add',
      campaign,
      body: { items: [{ id, quantity: 1, properties: { _gwp_campaign: campaign } }] },
    };
  },

  request(action) {
    this.busy = true;
    const url = window.Shopify.routes.root + (action.type === 'add' ? 'cart/add.js' : 'cart/update.js');

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...action.body, sections: this.sections }),
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        this.busy = false;
        if (!ok || data.status) {
          console.warn('[GWP] Cart request failed:', data.description || data.message || data);
          return;
        }

        if (action.type === 'add') {
          this.track('gwp_gift_added', action.campaign);
        } else {
          this.track('gwp_gift_removed', '');
        }

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && data.sections) cartDrawer.renderContents(data);
        if (typeof updateMultipleCartBubbles === 'function') updateMultipleCartBubbles();
      })
      .catch((error) => {
        this.busy = false;
        console.warn('[GWP] Cart request failed:', error);
      });
  },
};

class GwpCart extends HTMLElement {
  connectedCallback() {
    let state;
    try {
      state = JSON.parse(this.querySelector('[data-gwp-state]').textContent);
    } catch (e) {
      return;
    }

    if (state.empty) GWP.clearDeclined();

    const addButton = this.querySelector('[data-gwp-add]');
    if (addButton) {
      // With auto-add on, the button only shows for a gift the customer removed
      if (state.add && GWP.isDeclined(state.add.campaign)) addButton.hidden = false;

      addButton.addEventListener('click', () => {
        const campaign = addButton.dataset.campaign;
        addButton.disabled = true;
        addButton.textContent = 'Adding…';
        GWP.setDeclined(campaign, false);
        GWP.request(GWP.addAction(Number(addButton.dataset.variantId), campaign));
      });
    }

    GWP.trackQualified(state.qualified);
    GWP.reconcile(state);
  }
}

// Record gifts the customer removes so auto-add doesn't put them straight back
document.addEventListener(
  'click',
  (event) => {
    const giftLine = event.target.closest('[data-gwp-campaign]');
    if (giftLine && event.target.closest('cart-remove-button')) {
      GWP.setDeclined(giftLine.dataset.gwpCampaign, true);
    }
  },
  true
);

if (!customElements.get('gwp-cart')) customElements.define('gwp-cart', GwpCart);
